import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Content-shaped loading placeholders for the `/settings` sections. Padding and
 * block sizes are copied from the real component so nothing jumps on handover.
 * No card wrapper (the call site owns it); every block is `aria-hidden`.
 */

/** The same hairline every settings list row draws between its rows. */
const ROW_DIVIDER = '1px solid var(--ink-100)';

/** `borderBottom` for row `i` of `total`, suppressed on the last — as `SectionRow` does. */
function rowDivider(i: number, total: number): string | undefined {
  return i === total - 1 ? undefined : ROW_DIVIDER;
}

/**
 * Avatar-led member rows: circle, name over email, role pill, action.
 * Shapes `access/MemberList` and `people/PeopleSection`'s roster.
 */
export function MemberRowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="member-row-skeleton"
          className="flex items-center gap-3"
          style={{ padding: '14px 18px', borderBottom: rowDivider(i, rows) }}
        >
          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-[140px] max-w-full rounded" />
            <Skeleton className="h-3 w-[180px] max-w-full rounded" />
          </div>
          <Skeleton className="h-[22px] w-[72px] shrink-0 rounded-full" />
          <Skeleton className="h-8 w-[60px] shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/**
 * Icon-led list rows: square mark, title over meta, one trailing action.
 * Shapes `security/SessionsList`, `access/ShareableLinkPanel` and
 * `api-keys/ApiKeysSection` (with `scopeChips`).
 */
export function ListRowsSkeleton({
  rows = 3,
  scopeChips = false,
}: {
  rows?: number;
  /** Adds the small chip row the API-keys rows carry under their meta line. */
  scopeChips?: boolean;
}) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="list-row-skeleton"
          className="flex items-center gap-3"
          style={{ padding: '14px 18px', borderBottom: rowDivider(i, rows) }}
        >
          <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-[160px] max-w-full rounded" />
            <Skeleton className="h-3 w-[200px] max-w-full rounded" />
            {scopeChips && (
              <div className="flex flex-wrap gap-1.5">
                <Skeleton className="h-5 w-[64px] rounded-full" />
                <Skeleton className="h-5 w-[52px] rounded-full" />
              </div>
            )}
          </div>
          <Skeleton className="h-8 w-[72px] shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/**
 * Stacked label-above-control fields, matching `SectionField`'s recipe.
 * Shapes `general/WorkspaceSettingsSection` and other single-card form areas.
 */
export function FormFieldsSkeleton({
  fields = 2,
  className,
}: {
  fields?: number;
  className?: string;
}) {
  return (
    <div aria-hidden="true" className={cn('flex flex-col gap-4', className)}>
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} data-testid="form-field-skeleton" className="flex flex-col gap-1.5">
          <Skeleton className="h-3 w-[90px] rounded" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      ))}
    </div>
  );
}

/**
 * Workspace tiles: mark, name over meta, action, and the quota bar under it.
 * Shapes `workspaces/WorkspacesSection`'s `WorkspaceTileRow` list.
 */
export function WorkspaceTilesSkeleton({ tiles = 3 }: { tiles?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: tiles }, (_, i) => (
        <div
          key={i}
          data-testid="workspace-tile-skeleton"
          style={{ padding: '14px 18px', borderBottom: rowDivider(i, tiles) }}
        >
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-4 w-[150px] max-w-full rounded" />
              <Skeleton className="h-3 w-[220px] max-w-full rounded" />
            </div>
            <Skeleton className="h-8 w-[80px] shrink-0 rounded-md" />
          </div>
          <Skeleton className="mt-2.5 h-1.5 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * Category × channel rows: label over help, two centred switch blocks, on the
 * same `1fr 64px 64px` grid. Shapes `notifications/NotificationMatrix`.
 */
export function MatrixRowsSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="matrix-row-skeleton"
          className="grid items-center gap-3 px-[18px] py-3"
          style={{
            gridTemplateColumns: '1fr 64px 64px',
            borderBottom: rowDivider(i, rows),
          }}
        >
          <div className="flex min-w-0 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-[120px] max-w-full rounded" />
            <Skeleton className="h-3 w-[260px] max-w-full rounded" />
          </div>
          <div className="flex justify-center">
            <Skeleton className="h-5 w-[34px] rounded-full" />
          </div>
          <div className="flex justify-center">
            <Skeleton className="h-5 w-[34px] rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * One `SectionRow`: label over help, with a trailing action. Shapes Company's
 * "Owners" row, which is a single row — no avatar, no pill — so the member
 * shape would promise something that never arrives.
 */
export function SectionRowsSkeleton({ rows = 1 }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="section-row-skeleton"
          className="flex items-center gap-3"
          style={{ padding: '14px 18px', borderBottom: rowDivider(i, rows) }}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-[70px] max-w-full rounded" />
            <Skeleton className="h-3 w-[220px] max-w-full rounded" />
          </div>
          <Skeleton className="h-8 w-[120px] shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/**
 * A quota card: title line, progress bar, hint line. Shared by
 * `general/WorkspaceQuotaSummary` and `workspaces/OrgQuotaSummary`.
 */
export function QuotaSummarySkeleton() {
  return (
    <div aria-hidden="true" data-testid="quota-summary-skeleton" className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <Skeleton className="h-4 w-[150px] rounded" />
        <Skeleton className="h-3.5 w-[200px] max-w-full rounded" />
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
      <Skeleton className="h-3 w-[280px] max-w-full rounded" />
    </div>
  );
}

/**
 * The profile identity card: avatar row, divider, then the name/email fields.
 * Lives here so the route pending state can render it without importing
 * across sections.
 */
export function ProfileIdentitySkeleton() {
  return (
    <div className="flex flex-col gap-4" style={{ padding: 18 }}>
      <div className="flex flex-wrap items-center gap-3.5" aria-hidden="true">
        <Skeleton className="h-[54px] w-[54px] shrink-0 rounded-full" />
        <div className="flex flex-col gap-1.5" style={{ flex: '1 1 180px', minWidth: 180 }}>
          <Skeleton className="h-3.5 w-[110px] rounded" />
          <Skeleton className="h-3 w-full max-w-[320px] rounded" />
        </div>
        <Skeleton className="h-9 w-[84px] shrink-0 rounded-md" />
      </div>
      <div aria-hidden="true" style={{ height: 1, background: 'var(--ink-100)' }} />
      <FormFieldsSkeleton fields={2} />
    </div>
  );
}
